import React from 'react'
import { ScheduleWindow } from '@pushly/models/lib/enums/schedule-window'
import { getClassNames } from '../../../_utils/classnames'
import { BASE_TIME_FORMAT_WITHOUT_TZ } from '../../../constants'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { AppMessageBuilderSchedulePayload, MutableAppMessageBuilderSchedulePayload } from '../model-extensions'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { DatePicker, Radio, Skeleton, Space, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { AppMessageSettingRow } from './app-message-setting-row'

import './styles/app-message-schedule-builder.scss'

/**
 * How long a run window covers when an end date is first switched on. A one-day
 * window is almost never what anyone wants, and any literal date would rot into
 * the past, so this is always applied relative to the start date.
 */
export const DEFAULT_SCHEDULE_RUN_DAYS = 30

const displayScheduleWindow = (value: ScheduleWindow) => {
    switch (value) {
        case ScheduleWindow.STANDARD:
            return 'Fixed'
        case ScheduleWindow.TIMEZONE:
            return 'Subscriber Time Zone'
    }
}

const getStrategyDescription = (
    runStrategy: ScheduleWindow,
    dateTimeZone: string,
    endDateEnabled: boolean,
    dateStartStr?: string,
    dateEndStr?: string,
) => {
    const descriptors: string[] = [`The app message will start on ${dateStartStr}`]
    if (endDateEnabled && dateEndStr) {
        descriptors.push(`and run through ${dateEndStr}`)
    }
    if (runStrategy === ScheduleWindow.TIMEZONE) {
        descriptors.push(`in each subscriber's time zone`)
    } else {
        descriptors.push(`in the ${dateTimeZone} time zone`)
    }

    return `${descriptors.join(' ')}.`
}

/**
 * The schedule as one sentence, for the summary row that hosts these controls.
 * Built from the same describer the expanded form uses, so the collapsed and
 * expanded views cannot disagree.
 */
export const getScheduleSummary = (schedule: AppMessageBuilderSchedulePayload): string =>
    getStrategyDescription(
        schedule.runDateWindow,
        schedule.runDateTimeZone,
        !!schedule.runDateEndUtc,
        dayjs(schedule.runDateStartUtc).format(BASE_TIME_FORMAT_WITHOUT_TZ),
        schedule.runDateEndUtc ? dayjs(schedule.runDateEndUtc).format(BASE_TIME_FORMAT_WITHOUT_TZ) : undefined,
    )

export const AppMessageScheduleBuilder = () => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context

    const value = variants[selectedVariantIdx]

    const schedule = value.schedules?.[0] ?? new MutableAppMessageBuilderSchedulePayload()
    const { runDateWindow, runDateStartUtc, runDateEndUtc, runDateTimeZone } = schedule

    const now = dayjs()
    const today = now.clone().startOf('day')
    const dateStart = dayjs(runDateStartUtc)

    const [endDateEnabled, setEndDateEnabled] = React.useState(!!runDateEndUtc)

    const handleScheduleUpdate = (change: Partial<MutableAppMessageBuilderSchedulePayload>) => {
        const newSchedule = schedule.clone(change)

        if (change.runDateEndUtc === null) {
            newSchedule.runDateEndUtc = null
        }

        dispatch({ type: 'patch', entity: 'schedule', data: newSchedule })
    }

    const handleDateChange = (date: Dayjs | null, rangePoint: 'Start' | 'End') => {
        let nextDate = date
        const scheduleKey = `runDate${rangePoint}Utc`

        if (nextDate) {
            const prevDate = dayjs(schedule[scheduleKey])
            nextDate = nextDate.hour(prevDate.hour()).minute(prevDate.minute())
        }

        const startDate = !nextDate ? undefined : nextDate.utc().toISOString()
        handleScheduleUpdate({ [scheduleKey]: startDate })
    }

    const handleTimeChange = (time: dayjs.Dayjs | null, rangePoint: 'Start' | 'End') => {
        let nextDate
        const scheduleKey = `runDate${rangePoint}Utc`

        if (time) {
            nextDate = dayjs(schedule[scheduleKey])
            nextDate = nextDate.hour(time.hour()).minute(time.minute()).second(59)
        }

        const startDate = !nextDate ? undefined : nextDate.utc().toISOString()

        handleScheduleUpdate({ [scheduleKey]: startDate })
    }

    return (
        <div className={getClassNames('app-message-schedule-builder')}>
            <Skeleton loading={context.loading} active={true} avatar={true} title={false}>
                <AppMessageSettingRow label="Strategy">
                    <Radio.Group
                        aria-label="Schedule strategy"
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                        className="schedule-strategy-options"
                        disabled={context.disabled}
                        value={schedule.runDateWindow ?? ScheduleWindow.STANDARD}
                        onChange={(ev) => handleScheduleUpdate({ runDateWindow: ev.target.value })}
                        options={Object.values(ScheduleWindow).map((w) => ({
                            label: displayScheduleWindow(w),
                            value: ScheduleWindow[w],
                        }))}
                    />
                </AppMessageSettingRow>

                <AppMessageSettingRow label="Schedule Start">
                    <Space className="delivery-senddate-picker" size="small">
                        <DatePicker
                            className="delivery-date-picker"
                            format="ddd, MMM DD, YYYY"
                            placeholder="Start Date"
                            aria-label="Start date"
                            disabled={context.disabled}
                            disabledDate={(current) => !!current && current < today}
                            value={!dateStart ? dayjs().startOf('day') : dateStart}
                            onChange={(startDate) => {
                                handleDateChange(startDate, 'Start')
                            }}
                        />

                        <BetterTimePicker
                            className="delivery-time-picker"
                            format="h:mm a"
                            use12Hours={true}
                            placeholder="Time"
                            value={!dateStart ? now.clone().startOf('day') : dateStart}
                            disabled={context.disabled}
                            onTimeChange={(startTime) => handleTimeChange(startTime, 'Start')}
                        />
                        <Typography.Text type="secondary" className="schedule-timezone">
                            {runDateWindow === ScheduleWindow.TIMEZONE
                                ? "each subscriber's time zone"
                                : runDateTimeZone}
                        </Typography.Text>
                    </Space>
                </AppMessageSettingRow>

                <AppMessageSettingRow
                    label="End Date"
                    toggle={{
                        checked: endDateEnabled,
                        disabled: context.disabled,
                        onChange: (checked) => {
                            let endDate
                            if (!checked) {
                                endDate = null
                            } else {
                                // dayjs is immutable, so the shifted value has to
                                // be the one that is kept.
                                const seed = (dateStart ?? dayjs()).add(DEFAULT_SCHEDULE_RUN_DAYS, 'day').endOf('day')

                                endDate = seed.format('YYYY-MM-DD HH:mm:ss')
                            }

                            handleScheduleUpdate({ runDateEndUtc: endDate })
                            setEndDateEnabled(checked)
                        },
                    }}
                    offText="Runs until paused"
                >
                    <Space className="delivery-senddate-picker" size="small">
                        <DatePicker
                            className="delivery-date-picker"
                            format="ddd, MMM DD, YYYY"
                            placeholder="End Date"
                            aria-label="End date"
                            disabled={context.disabled}
                            disabledDate={(current) => !!dateStart && !!current && current < dateStart}
                            value={!runDateEndUtc ? undefined : dayjs(runDateEndUtc)}
                            onChange={(endDate) => handleDateChange(endDate, 'End')}
                        />

                        <BetterTimePicker
                            className="delivery-time-picker"
                            format="h:mm a"
                            use12Hours={true}
                            placeholder="Time"
                            disabled={context.disabled}
                            disabledDate={(current) => !!dateStart && current < dateStart.clone().add(1, 'day')}
                            value={!runDateEndUtc ? undefined : dayjs(runDateEndUtc)}
                            onTimeChange={(endTime) => handleTimeChange(endTime, 'End')}
                        />
                    </Space>
                </AppMessageSettingRow>
            </Skeleton>
        </div>
    )
}
